import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, ShieldCheck } from 'lucide-react';
import AuthContext from '../../store/auth-context';
import {
    approveImplant,
    getApprovedImplants,
    getImplantsForReapproval,
} from '../../services/apiService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MultiCombobox } from '../ui/multi-combobox';
import { DataTable, DataTableColumn } from '../ui/data-table';
import CopyButton from '../general/CopyButton';
import LoadingRing from '../ui-components/LoadingRing';
import SETTING_KEYS from '../../constants/setting-keys';
import styles from './ApprovalPage.module.css';
import Modal from '../manage-user/Modal';
import Implant from '../implant/Implant';
import { ImplantProvider } from '../../store/implant-context';
import ApprovalNote from './ApprovalNote';
import PERMISSION_KEYS from '../../constants/permission-keys';
import { EVENT_KEYS, trackLoad } from '../../services/api/trackingService';
import { toTitleCase } from '../../utils/utils';

const ApprovalPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const ctx = useContext(AuthContext);

    const [forReapproval, setForReapproval] = useState<any[]>([]);
    const [approved, setApproved] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [implantInPreview, setImplantInPreview] = useState('');
    const [commentInPreview, setCommentInPreview] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [selectedManufacturers, setSelectedManufacturers] = useState<
        string[]
    >([]);

    const getData = async () => {
        if (ctx.hasPermission(PERMISSION_KEYS.approve)) {
            const imps = await getImplantsForReapproval();
            setForReapproval(imps);
        }
        const imps = await getApprovedImplants();
        setApproved(Array.isArray(imps) ? imps : []);
        setIsLoading(false);
    };

    useEffect(() => {
        trackLoad(EVENT_KEYS.viewApprovedImplants, {});
        getData();
    }, []);

    const handleApprove = async (note: string) => {
        await approveImplant(implantInPreview, note);
        setImplantInPreview('');
        getData();
    };

    const getModel = (imp: any): string => imp.info?.[SETTING_KEYS.model] ?? '';
    const getType = (imp: any): string => imp.info?.[SETTING_KEYS.type] ?? '';
    const getManufacturer = (imp: any): string =>
        imp[SETTING_KEYS.manufacturer] ?? '';
    const getMriCode = (imp: any): string =>
        imp.info?.[SETTING_KEYS.mriCode] ?? '';

    const allTypes = useMemo(
        () =>
            Array.from(new Set(approved.map(getType).filter(Boolean))).sort(),
        [approved]
    );

    const allManufacturers = useMemo(
        () =>
            Array.from(
                new Set(
                    approved
                        .map((imp) => toTitleCase(getManufacturer(imp)))
                        .filter(Boolean)
                )
            ).sort(),
        [approved]
    );

    const visibleApproved = useMemo(() => {
        let result = approved;

        if (selectedTypes.length > 0) {
            result = result.filter((imp) =>
                selectedTypes.includes(getType(imp))
            );
        }

        if (selectedManufacturers.length > 0) {
            result = result.filter((imp) =>
                selectedManufacturers.includes(
                    toTitleCase(getManufacturer(imp))
                )
            );
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                (imp) =>
                    getModel(imp).toLowerCase().includes(term) ||
                    getType(imp).toLowerCase().includes(term) ||
                    getManufacturer(imp).toLowerCase().includes(term) ||
                    getMriCode(imp).toLowerCase().includes(term)
            );
        }

        return result;
    }, [approved, searchTerm, selectedTypes, selectedManufacturers]);

    const reapprovalColumns: DataTableColumn<any>[] = [
        {
            key: 'model',
            header: t('approval.awaitingApproval.headers.implant'),
            cell: (a) => a.model,
            sortValue: (a) => a.model,
            className: 'font-medium',
        },
        {
            key: 'approvedBy',
            header: t('approval.awaitingApproval.headers.approvedBy'),
            cell: (a) => a.approvedBy,
            sortValue: (a) => a.approvedBy,
            className: 'text-muted-foreground',
        },
        {
            key: 'approvedAt',
            header: t('approval.awaitingApproval.headers.approvedDate'),
            cell: (a) =>
                a.approvedAt
                    ? new Date(a.approvedAt).toLocaleDateString()
                    : '—',
            sortValue: (a) =>
                a.approvedAt ? new Date(a.approvedAt).getTime() : 0,
            className: 'text-muted-foreground',
        },
        {
            key: 'updatedAt',
            header: t('approval.awaitingApproval.headers.updatedDate'),
            cell: (a) =>
                a.updatedAt ? new Date(a.updatedAt).toLocaleDateString() : '—',
            sortValue: (a) =>
                a.updatedAt ? new Date(a.updatedAt).getTime() : 0,
            className: 'text-muted-foreground',
        },
        {
            key: 'actions',
            header: '',
            headClassName: 'w-44',
            className: 'text-right',
            cell: (a) => (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        setImplantInPreview(a.implant_id);
                        setCommentInPreview(a.comment);
                    }}
                >
                    {t('approval.awaitingApproval.button')}
                </Button>
            ),
        },
    ];

    const approvedColumns: DataTableColumn<any>[] = [
        {
            key: 'model',
            header: t('approval.approvedImplants.headers.model'),
            cell: getModel,
            sortValue: getModel,
            className: 'font-medium',
        },
        {
            key: 'type',
            header: t('approval.approvedImplants.headers.type'),
            cell: getType,
            sortValue: getType,
            className: 'text-muted-foreground',
        },
        {
            key: 'manufacturer',
            header: t('approval.approvedImplants.headers.manufacturer'),
            cell: (imp) => toTitleCase(getManufacturer(imp)),
            sortValue: getManufacturer,
            className: 'text-muted-foreground',
        },
        {
            key: 'mriCode',
            header: t('approval.approvedImplants.headers.mriCode'),
            cell: (imp) => {
                const mriCode = getMriCode(imp);
                return mriCode ? (
                    <div className="flex items-center gap-1">
                        <span>{mriCode}</span>
                        <CopyButton value={mriCode} />
                    </div>
                ) : (
                    <span className="text-muted-foreground/50">—</span>
                );
            },
            sortValue: getMriCode,
            className: 'text-muted-foreground',
        },
    ];

    return (
        <div className="container mx-auto max-w-6xl px-4 py-8">
            {implantInPreview !== '' && (
                <Modal
                    scrollable={true}
                    info={{
                        title: '',
                        message: '',
                    }}
                    updateView={() => {
                        setImplantInPreview('');
                    }}
                >
                    <ApprovalNote
                        title={t('approval.reapprove.title')}
                        description={t('approval.reapprove.description')}
                        defaultNote={commentInPreview}
                        onSubmit={handleApprove}
                    />
                    <div className={styles.preview}>
                        <ImplantProvider>
                            <Implant isPreview={true} id={implantInPreview} />
                        </ImplantProvider>
                    </div>
                </Modal>
            )}

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <LoadingRing size="large" />
                </div>
            ) : (
                <div className="flex flex-col gap-10">
                    {ctx.hasPermission(PERMISSION_KEYS.approve) &&
                        forReapproval.length > 0 && (
                            <div>
                                <h1 className="mb-6 text-2xl font-bold">
                                    {t('approval.awaitingApproval.title')}
                                </h1>
                                <DataTable
                                    columns={reapprovalColumns}
                                    items={forReapproval}
                                    rowKey={(a) => a.implant_id}
                                    onRowClick={(a) =>
                                        navigate('/implant/' + a.implant_id)
                                    }
                                />
                            </div>
                        )}

                    <div>
                        <h1 className="mb-6 text-2xl font-bold">
                            {t('approval.approvedImplants.title')}
                        </h1>

                        {approved.length === 0 ? (
                            <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                                <ShieldCheck className="mb-3 h-10 w-10 text-muted-foreground" />
                                <h2 className="mb-1 text-lg font-semibold">
                                    {t('approval.noApprovedImplants.title', {
                                        departmentName:
                                            ctx.getContext().department.name,
                                    })}
                                </h2>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                                    <div className="relative w-full sm:max-w-md">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder={t(
                                                'approval.filters.searchPlaceholder'
                                            )}
                                            value={searchTerm}
                                            onChange={(e) =>
                                                setSearchTerm(e.target.value)
                                            }
                                            className="pl-9"
                                        />
                                    </div>
                                    <MultiCombobox
                                        options={allTypes}
                                        value={selectedTypes}
                                        onChange={setSelectedTypes}
                                        placeholder={t(
                                            'approval.filters.typesPlaceholder'
                                        )}
                                        searchPlaceholder={t(
                                            'approval.filters.typesSearchPlaceholder'
                                        )}
                                        emptyText={t(
                                            'approval.filters.noTypes'
                                        )}
                                        className="w-full sm:w-56"
                                        aria-label="Filter by type"
                                    />
                                    <MultiCombobox
                                        options={allManufacturers}
                                        value={selectedManufacturers}
                                        onChange={setSelectedManufacturers}
                                        placeholder={t(
                                            'approval.filters.manufacturersPlaceholder'
                                        )}
                                        searchPlaceholder={t(
                                            'approval.filters.manufacturersSearchPlaceholder'
                                        )}
                                        emptyText={t(
                                            'approval.filters.noManufacturers'
                                        )}
                                        className="w-full sm:w-56"
                                        aria-label="Filter by manufacturer"
                                    />
                                </div>

                                {visibleApproved.length === 0 ? (
                                    <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                                        <Search className="mb-3 h-10 w-10 text-muted-foreground" />
                                        <p className="max-w-md text-sm text-muted-foreground">
                                            {t('approval.filters.noResults')}
                                        </p>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={approvedColumns}
                                        items={visibleApproved}
                                        rowKey={(imp) => imp.info._id}
                                        onRowClick={(imp) =>
                                            navigate(
                                                '/implant/' + imp.info._id
                                            )
                                        }
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApprovalPage;
